// clientService.js
import apiRequest, { getAdminApiHeaders } from './apiService'; // Import the generic API request function
import { wrapAdminPayload, isAdminPayloadProtectionEnabled } from './adminPayloadCrypto';

const getProtectedAdminHeaders = () => (
    isAdminPayloadProtectionEnabled()
        ? getAdminApiHeaders({ 'X-Zuntly-Payload': '1' })
        : getAdminApiHeaders()
);

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/zuntly/admin`;
const CLIENTS_ENDPOINT = "/clients"; // Base endpoint for clients
const SERVICES_ENDPOINT = "/services"; // Base endpoint for basic services
const PRODUCTS_ENDPOINT = "/products"; // Base endpoint for products
const THEMES_ENDPOINT = "/themes"; // Base endpoint for basic themes
const INVENTORY_ENDPOINT = "/inventory"; // Base endpoint for Inventory
const ORDERS_ENDPOINT = "/orders"; // Base endpoint for orders
const ANALYTICS_ENDPOINT = "/analytics"; // Base endpoint for analytics

// **branches page START **
export const fetchBranchesData = async () => {
    return apiRequest(`${CLIENTS_ENDPOINT}/getClientData`, 'POST', {});
};

export const activateBranch = async (clientId) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/reActiceClient`, 'POST', { _id: clientId });
};

export const deactivateBranch = async (clientId) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/deActiceClient`, 'POST', { _id: clientId });
};
// **branches page END **


// **domains page START **
export const fetchDomainsData = async () => {
    return apiRequest(`${SERVICES_ENDPOINT}/getDomains`, 'POST', {});
};

export const getDefaultDomains = async () => {
    return apiRequest(`${SERVICES_ENDPOINT}/getDefaultDomains`, 'POST', {});
};

export const updateDefaultDomains = async (domains) => {
    return apiRequest(`${SERVICES_ENDPOINT}/updateDefaultDomains`, 'POST', domains);
};



export const fetchFreeDomainsData = async (selectedDomainId) => {
    return apiRequest(`${SERVICES_ENDPOINT}/getFreeDomains`, 'POST', { selectedDomainId });
};

export const fetchBranchDataById = async (branchId) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/getClientBasedOnId`, 'POST', { _id: branchId });
};

/** Branch id for the current admin scope (selected branch for super admin, own id for branch admin). */
export const getScopedBranchIdForUser = () => {
    try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}');
        if (!user?._id) return null;
        if (user.role === 1 && user.selectedBranch) return user.selectedBranch;
        return user._id;
    } catch {
        return null;
    }
};

/** Marketplace is on when this branch has it enabled; child branches also require master enabled. */
export const resolveMarketplaceEnabled = (branch, masterBranch) => {
    if (!branch) return false;
    if (branch.masterAccount) return !!branch.enableMarketplace;
    const masterOn = masterBranch ? !!masterBranch.enableMarketplace : false;
    return !!branch.enableMarketplace && masterOn;
};

export const isBranchMarketplaceEnabled = async (branchIdToFetch) => {
    if (!branchIdToFetch) return false;
    try {
        const branchResp = await fetchBranchDataById(branchIdToFetch);
        if (!branchResp?.success || !branchResp.data) return false;
        const branch = branchResp.data;
        if (branch.masterAccount) return !!branch.enableMarketplace;
        if (branch.masterId) {
            const masterResp = await fetchBranchDataById(branch.masterId);
            const master = masterResp?.success ? masterResp.data : null;
            return !!branch.enableMarketplace && !!master?.enableMarketplace;
        }
        return !!branch.enableMarketplace;
    } catch {
        return false;
    }
};

export const updateBranchData = async (branchData) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/updateClient`, 'POST', branchData);
};

export const toggleAutoConfirmOnlineOrders = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleAutoConfirmOnlineOrders`, 'POST', data);
};

// Flash Discount: Single-Product Mode (per master/branch)
export const getFlashDiscountSettings = async () => {
    return apiRequest(`${CLIENTS_ENDPOINT}/getFlashDiscountSettings`, 'POST', {});
};

export const toggleFlashDiscountSingleProductMode = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleFlashDiscountSingleProductMode`, 'POST', data);
};

export const toggleFlashDiscountShowOnHome = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleFlashDiscountShowOnHome`, 'POST', data);
};

export const toggleFlashDealAutoScroll = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleFlashDealAutoScroll`, 'POST', data);
};

/** Control Centre: flash toggles + Hide Timeline (customer app) */
export const getControlCentreSettings = async () => {
    return apiRequest(`${CLIENTS_ENDPOINT}/getControlCentreSettings`, 'POST', {});
};

export const toggleHideProductDeliveryTimeline = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleHideProductDeliveryTimeline`, 'POST', data);
};

export const toggleHideStickyCategoryRow = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleHideStickyCategoryRow`, 'POST', data);
};

export const toggleShowSubCategoriesInStickyCategory = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleShowSubCategoriesInStickyCategory`, 'POST', data);
};

export const toggleShowSubCategoriesInMainCategory = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleShowSubCategoriesInMainCategory`, 'POST', data);
};

export const toggleHideMainCategory = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleHideMainCategory`, 'POST', data);
};

export const toggleGlobalCartLimit = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleGlobalCartLimit`, 'POST', data);
};

export const updateGlobalMinimumCartValue = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/updateGlobalMinimumCartValue`, 'POST', data);
};

export const createBranchData = async (branchData) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/createClient`, 'POST', branchData);
};

export const updateCustomProductLimit = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/updateCustomProductLimit`, 'POST', data);
};

export const updateCustomBranchLimit = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/updateCustomBranchLimit`, 'POST', data);
};

export const toggleEnableFreeCash = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleEnableFreeCash`, 'POST', data);
};

export const updateBrainDailyRequestLimit = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/updateBrainDailyRequestLimit`, 'POST', data);
};

export const updateZuntlyAiDailyRequestLimit = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/updateZuntlyAiDailyRequestLimit`, 'POST', data);
};


//  **coupon page START **
export const getCoupons = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/getCoupons`, 'POST', data);
};

export const createCoupon = async (couponData) => {
    return apiRequest(`${SERVICES_ENDPOINT}/createCoupon`, 'POST', couponData);
};

export const updateCoupon = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/updateCoupon`, 'POST', data);
};

export const reActivate = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/reActiveCoupon`, 'POST', data);
};

export const deActivateCoupon = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/deActiveCoupon`, 'POST', data);
};

export const deleteCoupon = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/deleteCoupon`, 'POST', data);
};

export const generateCouponDescription = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/generateCouponDescription`, 'POST', data);
};
//  **coupon page END **


//  **categories page START **
export const createCatogory = async (couponData) => {
    return apiRequest(`${SERVICES_ENDPOINT}/createCatogory`, 'POST', couponData);
};

export const getCategories = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/getCategories`, 'POST', data);
};

export const updateCategory = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/updateCategory`, 'POST', data);
};

export const deleteCategory = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/deleteCategory`, 'POST', data);
}

export const toggleCategoryProductsActive = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/toggleCategoryProductsActive`, 'POST', data);
}

export const toggleCategoryProductsStock = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/toggleCategoryProductsStock`, 'POST', data);
}

export const getCategoryProductCounts = async (data) => {
    return apiRequest(`${SERVICES_ENDPOINT}/getCategoryProductCounts`, 'POST', data);
}
//  **categories page START **


//  **products page START **
export const getDeliveryOptions = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getDeliveryOptions`, 'POST', data);
};

export const getMasterProductCounts = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getMasterProductCounts`, 'POST', data || {});
};

export const getProducts = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getProducts`, 'POST', data);
};

// POS Billing (Premium) - paginated offline catalogue with category/search/skip-limit
export const getPosBillingProducts = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getPosBillingProducts`, 'POST', data || {});
};

export const applyManualProductRatings = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/applyManualProductRatings`, 'POST', data);
};

export const getProductManualRatingTotals = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getProductManualRatingTotals`, 'POST', data);
};

export const updateProductOptionExpiry = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/updateProductOptionExpiry`, 'POST', data);
};

export const getProductExpiriesOverview = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getProductExpiriesOverview`, 'POST', data);
};

export const getProductsWithoutExpiry = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getProductsWithoutExpiry`, 'POST', data || {});
};

/** Paginated list of product options whose expiry date is already past. */
export const getExpiredProductOptions = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getExpiredProductOptions`, 'POST', data || {});
};

/** Count of product options whose expiry date is already past (for sidebar badge). */
export const getExpiredProductOptionsCount = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getExpiredProductOptionsCount`, 'POST', data || {});
};

export const createProduct = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/createProduct`, 'POST', data);
};

/** Download Bulk Products sample Excel (.xlsx buffer). */
export const downloadBulkProductSample = async () => {
    const headers = getProtectedAdminHeaders();
    const response = await fetch(`${API_BASE_URL}${PRODUCTS_ENDPOINT}/downloadBulkProductSample`, {
        method: 'POST',
        headers,
        body: JSON.stringify(await wrapAdminPayload({})),
    });
    if (!response.ok) throw new Error('Failed to download sample Excel');
    return response.blob();
};

export const validateBulkProducts = async (rows) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/validateBulkProducts`, 'POST', { rows });
};

export const bulkCreateProducts = async (products) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/bulkCreateProducts`, 'POST', { products });
};

export const getPreImage = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getPresignedUrl`, 'POST', data);
};

export const deleteImageFromS3 = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/deleteImage`, 'POST', data);
};

export const updateProduct = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/updateProduct`, 'POST', data);
}

export const deleteProduct = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/deleteProduct`, 'POST', data);
}

export const ActiveAndOutOfStock = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/ActiveAndOutOfStock`, 'POST', data);
}

export const generateProductDescription = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/generateProductDescription`, 'POST', data);
}

export const getLastProductDetailsTabs = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/getLastProductDetailsTabs`, 'POST', data || {});
}

export const chatbot = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/chatbot`, 'POST', { messages: data.messages });
}

export const helpchatbot = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/helpchatbot`, 'POST', { messages: data.messages });
}

//  **products page END **


//  **Inventory page START **
export const getLocalBranchNamesForInventory = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/getLocalBranchNamesForInventory`, 'POST', data);
};

export const getInventoryDataForBranch = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/getInventoryDataForBranch`, 'POST', data);
};

export const updateInventoryDataForBranch = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/updateInventoryDataForBranch`, 'POST', data);
};

export const bulkDistributeInventory = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/bulkDistributeInventory`, 'POST', data);
};

// Zuntly Brain - Inventory Mode APIs
export const brainGetExamples = async () => {
    return apiRequest(`/brain/inventory/examples`, 'GET');
};

export const brainGetDailyUsage = async () => {
    return apiRequest(`/brain/daily-usage`, 'GET');
};

export const brainResolveInventory = async (data) => {
    return apiRequest(`/brain/inventory/resolve`, 'POST', data);
};

export const brainExecuteInventory = async (data) => {
    return apiRequest(`/brain/inventory/execute`, 'POST', data);
};

export const brainExecuteDistribution = async (data) => {
    return apiRequest(`/brain/inventory/distribute`, 'POST', data);
};

export const brainSearchProducts = async (data) => {
    return apiRequest(`/brain/inventory/search-products`, 'POST', data);
};

export const brainBuildLines = async (data) => {
    return apiRequest(`/brain/inventory/build-lines`, 'POST', data);
};

export const brainGetExpiryExamples = async () => {
    return apiRequest(`/brain/expiry/examples`, 'GET');
};

export const brainResolveExpiry = async (data) => {
    return apiRequest(`/brain/expiry/resolve`, 'POST', data);
};

export const brainExecuteExpiry = async (data) => {
    return apiRequest(`/brain/expiry/execute`, 'POST', data);
};

// Inventory Approval Workflow APIs
export const createDistributionRequest = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/createDistributionRequest`, 'POST', data);
};

export const createBranchStockRequest = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/createBranchStockRequest`, 'POST', data);
};

export const getPendingApprovals = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/getPendingApprovals`, 'POST', data);
};

export const getRequestDetails = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/getRequestDetails`, 'POST', data);
};

export const submitBranchReview = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/submitBranchReview`, 'POST', data);
};

export const approveFinalDistribution = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/approveFinalDistribution`, 'POST', data);
};

export const rejectDistribution = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/rejectDistribution`, 'POST', data);
};

export const getApprovalCounts = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/getApprovalCounts`, 'POST', data);
};

export const getDistributionHistory = async (data) => {
    return apiRequest(`${INVENTORY_ENDPOINT}/getDistributionHistory`, 'POST', data);
};
//  **Inventory page END **

//  **Dynamic Footer page START **
export const getDynamicFooter = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/getDynamicFooter`, 'POST', data);
};

export const addUpdateFooter = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/addUpdateFooter`, 'POST', data);
};
//  **Dynamic Footer page END **


//  **Banners page START **
export const getOnlyProductNames = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/getOnlyProductNames`, 'POST', data);
};

export const getBanners = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/getBanners`, 'POST', data);
};

export const createBanner = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/createBanner`, 'POST', data);
};

export const updateBanner = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/updateBanner`, 'POST', data);
};

export const deleteBanner = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/deleteBanner`, 'POST', data);
};

export const updateBannerOrder = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/updateBannerOrder`, 'POST', data);
};
//  **Banners page END **

//  **Search Names START **
export const saveSearchNames = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/saveSearchNames`, 'POST', data);
};

export const getSearchNames = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/getSearchNames`, 'POST', data);
};
//  **Search Names END **


//  **ShowcaseSections page START **
export const getThemeColor = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/getThemeColor`, 'POST', data);
};

export const updateThemeColor = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/updateThemeColor`, 'POST', data);
};

export const createShowcaseSection = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/createShowcaseSection`, 'POST', data);
};

export const updateShowcaseSection = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/updateShowcaseSection`, 'POST', data);
};

export const deActiveShowcaseSection = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/deActiveShowcaseSection`, 'POST', data);
};

export const reActiveShowcaseSection = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/reActiveShowcaseSection`, 'POST', data);
};

export const getShowcaseSections = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/getShowcaseSections`, 'POST', data);
};

export const deleteShowcaseSection = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/deleteShowcaseSection`, 'POST', data);
};

export const updateShowcaseSectionOrder = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/updateShowcaseSectionOrder`, 'POST', data);
};

export const saveGalleryImages = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/saveGalleryImages`, 'POST', data);
};

export const getGalleryImages = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/getGalleryImages`, 'POST', data);
};

// **Category Customization START **
export const getCategoryCustomization = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/getCategoryCustomization`, 'POST', data);
};

export const saveCategoryCustomization = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/saveCategoryCustomization`, 'POST', data);
};
// **Category Customization END **

// **Home Theme START **
export const getHomeTheme = async () => {
    return apiRequest(`${THEMES_ENDPOINT}/getHomeTheme`, 'POST', {});
};

export const saveHomeTheme = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/saveHomeTheme`, 'POST', data);
};
// **Home Theme END **

// **Design Image Links START **
export const getDesignImageLinks = async () => {
    return apiRequest(`${THEMES_ENDPOINT}/getDesignImageLinks`, 'POST', {});
};

export const saveDesignImageLink = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/saveDesignImageLink`, 'POST', data);
};

export const deleteDesignImageLink = async (data) => {
    return apiRequest(`${THEMES_ENDPOINT}/deleteDesignImageLink`, 'POST', data);
};
// **Design Image Links END **

//  **ShowcaseSections page END **

//  **orders page START **
export const getOrders = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/getBranchOrders`, 'POST', data);
};

export const getDeliveredOrdersTotals = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/getDeliveredOrdersTotals`, 'POST', data || {});
};

export const changeOrderStatus = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/changeOrderStatus`, 'POST', data);
};

export const cancelOrderItem = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/cancelOrderItem`, 'POST', data);
};

export const updateItemStatus = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/updateItemStatus`, 'POST', data);
};

// ** Order Notifications START **
export const getNewOrdersNotification = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/getNewOrdersNotification`, 'POST', data);
};

export const markOrdersAsViewed = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/markOrdersAsViewed`, 'POST', data);
};

export const getNotificationPreferences = async () => {
    return apiRequest(`${ORDERS_ENDPOINT}/getNotificationPreferences`, 'POST', {});
};
// ** Order Notifications END **
//  **orders page END **

// **Post-Order Command Center START **
const POST_ORDER_ENDPOINT = "/postOrder";

export const getOrderTimeline = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/getOrderTimeline`, 'POST', data);
};

export const flagException = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/flagException`, 'POST', data);
};

export const resolveException = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/resolveException`, 'POST', data);
};

export const addOrderNote = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/addNote`, 'POST', data);
};

export const assignAccountability = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/assignAccountability`, 'POST', data);
};

export const getPostOrderDashboard = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/getDashboard`, 'POST', data);
};

export const getPostOrderAnalytics = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/getAnalytics`, 'POST', data);
};

export const getReturnRequests = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/getReturnRequests`, 'POST', data);
};

export const getReturnRequestDetail = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/getReturnDetail`, 'POST', data);
};

export const updateReturnStatus = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/updateReturnStatus`, 'POST', data);
};

export const submitReturnOnBehalf = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/submitReturnOnBehalf`, 'POST', data);
};

export const getPostOrderSettings = async () => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/getSettings`, 'POST', {});
};

export const updatePostOrderSettings = async (data) => {
    return apiRequest(`${POST_ORDER_ENDPOINT}/updateSettings`, 'POST', data);
};
// **Post-Order Command Center END **

// GST Groups API
export const fetchGstGroups = async () => {
    return apiRequest(`${SERVICES_ENDPOINT}/getGstGroups`, 'POST', {});
};

export const createGstGroups = async (gstGroups) => {
    return apiRequest(`${SERVICES_ENDPOINT}/createGstGroup`, 'POST', gstGroups );
};

export const updateGstGroups = async (gstGroups) => {
    return apiRequest(`${SERVICES_ENDPOINT}/updateGstGroup`, 'POST',  gstGroups );
};

export const deleteGstGroups = async (gstGroups) => {
    return apiRequest(`${SERVICES_ENDPOINT}/deleteGstGroup`, 'POST',  gstGroups );
};

// **permissions START **
export const getPermissions = async () => {
    return apiRequest(`${SERVICES_ENDPOINT}/getPermissions`, 'POST', {});
};

export const updatePermissions = async (permissions) => {
    return apiRequest(`${SERVICES_ENDPOINT}/updatePermissions`, 'POST', permissions);
};
// **permissions END **

// Dashboard home metrics (kept — used by Dashboard.js, not the removed Analytics page)
export const getDashboardMetrics = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getDashboardMetrics`, 'POST', data);
};

// **Merchant Billing START **
export const lookupBarcode = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/lookupBarcode`, 'POST', data);
};

export const validateCartStock = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/validateCartStock`, 'POST', data);
};

export const verifyBarcode = async (data) => {
    return apiRequest(`${PRODUCTS_ENDPOINT}/verifyBarcode`, 'POST', data);
};

export const createWalkInOrder = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/createWalkInOrder`, 'POST', data);
};

export const lookupWalkInOrder = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/lookupWalkInOrder`, 'POST', data || {});
};

export const listWalkInOrdersForReturn = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/listWalkInOrdersForReturn`, 'POST', data || {});
};

export const returnWalkInOrderItems = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/returnWalkInOrderItems`, 'POST', data || {});
};
// **Merchant Billing END **

// **Walk-In Analytics START **
export const getWalkInAnalytics = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getWalkInAnalytics`, 'POST', data);
};

export const getWalkInCustomerAnalytics = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getWalkInCustomerAnalytics`, 'POST', data || {});
};

export const searchPosCustomerMobiles = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/searchPosCustomerMobiles`, 'POST', data || {});
};

// **POS Offers START **
export const getPosOfferCatalog = async (data) =>
    apiRequest('/posOffers/getCatalog', 'POST', data || {});

export const listPosOffers = async (data) =>
    apiRequest('/posOffers/list', 'POST', data || {});

export const createPosOffer = async (data) =>
    apiRequest('/posOffers/create', 'POST', data || {});

export const updatePosOffer = async (data) =>
    apiRequest('/posOffers/update', 'POST', data || {});

export const deletePosOffer = async (data) =>
    apiRequest('/posOffers/delete', 'POST', data || {});

export const evaluatePosOffers = async (data) =>
    apiRequest('/posOffers/evaluate', 'POST', data || {});
// **POS Offers END **

export const downloadWalkInCustomerAnalyticsPDF = async (data) => {
    try {
        const headers = getProtectedAdminHeaders();
        const response = await fetch(`${API_BASE_URL}${ANALYTICS_ENDPOINT}/downloadWalkInCustomerAnalyticsPDF`, {
            method: 'POST',
            headers,
            body: JSON.stringify(await wrapAdminPayload(data || {})),
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Customer Analytics PDF Error:', errorText);
            throw new Error('Failed to download PDF');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pos-customer-analytics-${data?.timePeriod?.toLowerCase?.()?.replace(/\s+/g, '-') || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        return { success: true, message: 'PDF downloaded successfully' };
    } catch (error) {
        console.error('Download Customer Analytics PDF Error:', error);
        throw error;
    }
};

export const getAllProductsWithSales = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getAllProductsWithSales`, 'POST', data);
};

export const getWalkInReturns = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getWalkInReturns`, 'POST', data || {});
};

export const getProductOptionsBreakdown = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getProductOptionsBreakdown`, 'POST', data);
};

export const downloadWalkInAnalyticsPDF = async (data) => {
    try {
        const headers = getProtectedAdminHeaders();
        const response = await fetch(`${API_BASE_URL}${ANALYTICS_ENDPOINT}/downloadWalkInAnalyticsPDF`, {
            method: 'POST',
            headers,
            body: JSON.stringify(await wrapAdminPayload(data || {})),
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('PDF Download Error:', errorText);
            throw new Error('Failed to download PDF');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Generate filename based on filters
        let filename = `walk-in-analytics-${data.timePeriod?.toLowerCase() || 'report'}`;
        if (data.startTime && data.endTime) {
            filename += `-${data.startTime.replace(':', '')}-${data.endTime.replace(':', '')}`;
        }
        if (data.customStartDate && data.customEndDate) {
            filename += `-${data.customStartDate}-to-${data.customEndDate}`;
        }
        filename += `-${new Date().toISOString().split('T')[0]}.pdf`;
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        return { success: true, message: 'PDF downloaded successfully' };
    } catch (error) {
        console.error('Download PDF Error:', error);
        throw error;
    }
};

export const getDetailedOrdersForExcel = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getDetailedOrdersForExcel`, 'POST', data);
};
// **Walk-In Analytics END **

// **Template Manager START **
export const fetchTemplatePreferences = async () => {
    return apiRequest(`${CLIENTS_ENDPOINT}/getTemplatePreferences`, 'POST', {});
};

export const saveTemplatePreferences = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/saveTemplatePreferences`, 'POST', data);
};
// **Template Manager END **


// **Ratings & Reviews (1-Star Support Tickets) START **
export const get1StarReviews = async (filters = {}) => {
    return apiRequest(`/ratings/get1StarReviews`, 'POST', filters);
};

// Get single ticket by orderId for modal refresh
export const getTicketByOrderId = async (orderId) => {
    return apiRequest(`/ratings/getTicketByOrderId`, 'POST', { orderId });
};

// Resolve all ratings for an order (pass orderId) or single rating (pass ratingId)
export const resolveRating = async (ratingId, orderId = null) => {
    return apiRequest(`/ratings/resolveRating`, 'POST', { ratingId, orderId });
};

// Unresolve all ratings for an order (pass orderId) or single rating (pass ratingId)
export const unresolveRating = async (ratingId, orderId = null) => {
    return apiRequest(`/ratings/unresolveRating`, 'POST', { ratingId, orderId });
};
// **Ratings & Reviews END **


// **Purchase Order & Vendor Management START **
const PURCHASE_ORDER_ENDPOINT = "/purchaseOrder";

// Vendor APIs
export const getVendors = async (filters = {}) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/getVendors`, 'POST', filters);
};

export const createVendor = async (vendorData) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/createVendor`, 'POST', vendorData);
};

export const updateVendor = async (vendorData) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/updateVendor`, 'POST', vendorData);
};

export const deleteVendor = async (vendorId) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/deleteVendor`, 'POST', { _id: vendorId });
};

// Purchase Order APIs
export const getPurchaseOrders = async (filters = {}) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/getPurchaseOrders`, 'POST', filters);
};

export const getProductsForPO = async () => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/getProductsForPO`, 'POST', {});
};

export const createPurchaseOrder = async (poData) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/createPurchaseOrder`, 'POST', poData);
};

export const updatePurchaseOrder = async (poData) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/updatePurchaseOrder`, 'POST', poData);
};

export const deletePurchaseOrder = async (poId) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/deletePurchaseOrder`, 'POST', { _id: poId });
};

export const acceptPurchaseOrder = async (poId) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/acceptPurchaseOrder`, 'POST', { _id: poId });
};

export const updatePOStatus = async (poId, status) => {
    return apiRequest(`${PURCHASE_ORDER_ENDPOINT}/updatePOStatus`, 'POST', { _id: poId, status });
};

export const downloadPOPDF = async (poId) => {
    const headers = getProtectedAdminHeaders();
    const response = await fetch(`${API_BASE_URL}${PURCHASE_ORDER_ENDPOINT}/downloadPOPDF`, {
        method: 'POST',
        headers,
        body: JSON.stringify(await wrapAdminPayload({ _id: poId })),
    });
    
    if (!response.ok) throw new Error('Failed to download PDF');
    
    const blob = await response.blob();
    return blob;
};

export const downloadPOExcel = async (poId) => {
    const headers = getProtectedAdminHeaders();
    const response = await fetch(`${API_BASE_URL}${PURCHASE_ORDER_ENDPOINT}/downloadPOExcel`, {
        method: 'POST',
        headers,
        body: JSON.stringify(await wrapAdminPayload({ _id: poId })),
    });
    
    if (!response.ok) throw new Error('Failed to download Excel');
    
    const blob = await response.blob();
    return blob;
};
// **Purchase Order & Vendor Management END **

// **Metrics Management START **
const METRICS_ENDPOINT = "/metrics"; // Base endpoint for metrics

export const getMetrics = async (data) => {
    return apiRequest(`${METRICS_ENDPOINT}/getMetrics`, 'POST', data);
};

export const getMetricsForProducts = async (data) => {
    return apiRequest(`${METRICS_ENDPOINT}/getMetricsForProducts`, 'POST', data);
};

export const createMetric = async (data) => {
    return apiRequest(`${METRICS_ENDPOINT}/createMetric`, 'POST', data);
};

export const updateMetric = async (data) => {
    return apiRequest(`${METRICS_ENDPOINT}/updateMetric`, 'POST', data);
};

export const deleteMetric = async (data) => {
    return apiRequest(`${METRICS_ENDPOINT}/deleteMetric`, 'POST', data);
};
// **Metrics Management END **

// **Inventory Logs Management START **
const INVENTORY_LOGS_ENDPOINT = "/inventoryLogs";

export const getInventoryLogs = async (data) => {
    return apiRequest(`${INVENTORY_LOGS_ENDPOINT}/getLogs`, 'POST', data);
};

export const getInventoryLogsSummary = async (data) => {
    return apiRequest(`${INVENTORY_LOGS_ENDPOINT}/getSummary`, 'POST', data);
};

export const getProductInventoryHistory = async (data) => {
    return apiRequest(`${INVENTORY_LOGS_ENDPOINT}/getProductHistory`, 'POST', data);
};

export const getLogFilterOptions = async (data) => {
    return apiRequest(`${INVENTORY_LOGS_ENDPOINT}/getFilterOptions`, 'POST', data);
};

export const exportInventoryLogs = async (data) => {
    const headers = getProtectedAdminHeaders();
    const response = await fetch(`${API_BASE_URL}${INVENTORY_LOGS_ENDPOINT}/export`, {
        method: 'POST',
        headers,
        body: JSON.stringify(await wrapAdminPayload(data || {})),
    });
    
    if (!response.ok) throw new Error('Failed to export inventory logs');
    
    const blob = await response.blob();
    return blob;
};
// **Inventory Logs Management END **

// **Customers Management START **
const CUSTOMERS_ENDPOINT = "/customers";

export const getCustomers = async (data) => {
    return apiRequest(`${CUSTOMERS_ENDPOINT}/getCustomers`, 'POST', data);
};

export const getAllCustomerMobileNumbers = async (data) => {
    return apiRequest(`${CUSTOMERS_ENDPOINT}/getAllMobileNumbers`, 'POST', data);
};

export const getAbandonedCarts = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getAbandonedCarts`, 'POST', data || {});
};

export const getCustomerBehaviorInsights = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getCustomerBehaviorInsights`, 'POST', data || {});
};

export const getLiveCustomerPresence = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getLiveCustomerPresence`, 'POST', data || {});
};

export const getTodayCustomerSessionHistory = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getTodayCustomerSessionHistory`, 'POST', data || {});
};

export const getBranchPerformanceInsights = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getBranchPerformanceInsights`, 'POST', data || {});
};

export const getFreeCashAnalytics = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getFreeCashAnalytics`, 'POST', data || {});
};

export const getFreeCashNotifiedNoUsage = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getFreeCashNotifiedNoUsage`, 'POST', data || {});
};

export const getInstoreSalesAnalytics = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getInstoreSalesAnalytics`, 'POST', data || {});
};

export const getInstoreCustomerAnalytics = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getInstoreCustomerAnalytics`, 'POST', data || {});
};

export const searchInstorePosCustomers = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/searchInstorePosCustomers`, 'POST', data || {});
};

export const getInstorePosCustomerProfile = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getInstorePosCustomerProfile`, 'POST', data || {});
};

export const getDeadProductsNoPurchase = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getDeadProductsNoPurchase`, 'POST', data || {});
};

export const getBranchNamesForUser = async () => {
    return apiRequest(`${CLIENTS_ENDPOINT}/getBranchNamesForUser`, 'POST', {});
};

export const getCustomerBehaviorTimeline = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getCustomerBehaviorTimeline`, 'POST', data || {});
};

export const getAddressById = async (data) => {
    return apiRequest(`${ANALYTICS_ENDPOINT}/getAddressById`, 'POST', data || {});
};

// **Product Variant Mappings START **
const PRODUCT_VARIANTS_ENDPOINT = "/productVariants";

export const getProductVariantMappings = async (data) => {
    return apiRequest(`${PRODUCT_VARIANTS_ENDPOINT}/getMappings`, 'POST', data);
};

export const getProductsForVariantMapping = async (data) => {
    return apiRequest(`${PRODUCT_VARIANTS_ENDPOINT}/getProductsForMapping`, 'POST', data);
};

export const createProductVariantMapping = async (data) => {
    return apiRequest(`${PRODUCT_VARIANTS_ENDPOINT}/createMapping`, 'POST', data);
};

export const updateProductVariantMapping = async (data) => {
    return apiRequest(`${PRODUCT_VARIANTS_ENDPOINT}/updateMapping`, 'POST', data);
};

export const deleteProductVariantMapping = async (data) => {
    return apiRequest(`${PRODUCT_VARIANTS_ENDPOINT}/deleteMapping`, 'POST', data);
};
// **Product Variant Mappings END **

// **Product Add-On Mappings START **
const PRODUCT_ADDONS_ENDPOINT = "/productAddons";

export const getProductAddonMappings = async (data) => {
    return apiRequest(`${PRODUCT_ADDONS_ENDPOINT}/getMappings`, 'POST', data);
};

export const getProductsForAddonMapping = async (data) => {
    return apiRequest(`${PRODUCT_ADDONS_ENDPOINT}/getProductsForMapping`, 'POST', data);
};

export const createProductAddonMapping = async (data) => {
    return apiRequest(`${PRODUCT_ADDONS_ENDPOINT}/createMapping`, 'POST', data);
};

export const updateProductAddonMapping = async (data) => {
    return apiRequest(`${PRODUCT_ADDONS_ENDPOINT}/updateMapping`, 'POST', data);
};

export const deleteProductAddonMapping = async (data) => {
    return apiRequest(`${PRODUCT_ADDONS_ENDPOINT}/deleteMapping`, 'POST', data);
};
// **Product Add-On Mappings END **

// **Timer Deals START **
const TIMER_DEALS_ENDPOINT = "/timerDeals";

export const getTimerDeals = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/getDeals`, 'POST', data || {});
};

export const getProductsForTimerDeal = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/getProducts`, 'POST', data);
};

export const createTimerDeal = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/createDeal`, 'POST', data);
};

export const updateTimerDealMeta = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/updateDealMeta`, 'POST', data);
};

export const addProductToTimerDeal = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/addProduct`, 'POST', data);
};

export const updateTimerDealProduct = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/updateProduct`, 'POST', data);
};

export const removeProductFromTimerDeal = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/removeProduct`, 'POST', data);
};

export const deleteTimerDeal = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/deleteDeal`, 'POST', data);
};

export const toggleTimerDealSlideUpView = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/toggleSlideUpView`, 'POST', data);
};

export const toggleTimerDealAutoScroll = async (data) => {
    return apiRequest(`${TIMER_DEALS_ENDPOINT}/toggleAutoScroll`, 'POST', data);
};

export const toggleTimerDealShowOnHome = async (data) => {
    return apiRequest(`${CLIENTS_ENDPOINT}/toggleTimerDealShowOnHome`, 'POST', data);
};
// **Timer Deals END **

// **Flash Deals (portal) START **
const FLASH_DEALS_ENDPOINT = "/flashDeals";

export const getFlashDealProducts = async (data) => {
    return apiRequest(`${FLASH_DEALS_ENDPOINT}/getProducts`, 'POST', data || {});
};

export const getProductsForFlashDealMapping = async (data) => {
    return apiRequest(`${FLASH_DEALS_ENDPOINT}/getProductsForMapping`, 'POST', data);
};

export const addFlashDealProduct = async (data) => {
    return apiRequest(`${FLASH_DEALS_ENDPOINT}/addProduct`, 'POST', data);
};

export const updateFlashDealProduct = async (data) => {
    return apiRequest(`${FLASH_DEALS_ENDPOINT}/updateProduct`, 'POST', data);
};

export const removeFlashDealProduct = async (data) => {
    return apiRequest(`${FLASH_DEALS_ENDPOINT}/removeProduct`, 'POST', data);
};
// **Flash Deals (portal) END **


// **Push Notifications - Notification Wrapper System START **
const NOTIFICATIONS_ENDPOINT = "/notifications";

// Template Management
export const getNotificationTemplates = async () => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/getTemplates`, 'POST', {});
};

export const createNotificationTemplate = async (data) => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/createTemplate`, 'POST', data);
};

export const updateNotificationTemplate = async (data) => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/updateTemplate`, 'POST', data);
};

export const deleteNotificationTemplate = async (data) => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/deleteTemplate`, 'POST', data);
};

// Automation Settings
export const getNotificationSettings = async () => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/getSettings`, 'POST', {});
};

export const updateNotificationSettings = async (data) => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/updateSettings`, 'POST', data);
};

// Manual Notifications
export const sendManualNotification = async (data) => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/send`, 'POST', data);
};

export const getNotificationFilteredCustomers = async (data) => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/getFilteredCustomers`, 'POST', data);
};

// Campaign History
export const getNotificationCampaignHistory = async (data) => {
    return apiRequest(`${NOTIFICATIONS_ENDPOINT}/getCampaignHistory`, 'POST', data);
};
// **Push Notifications - Notification Wrapper System END **

// **Free Cash Wallet START **
const FREE_CASH_ENDPOINT = "/freeCash";

export const getFreeCashAccess = async () => {
    return apiRequest(`${FREE_CASH_ENDPOINT}/getAccess`, 'POST', {});
};

export const getFreeCashSettings = async () => {
    return apiRequest(`${FREE_CASH_ENDPOINT}/getSettings`, 'POST', {});
};

export const updateFreeCashSettings = async (data) => {
    return apiRequest(`${FREE_CASH_ENDPOINT}/updateSettings`, 'POST', data);
};

export const getFreeCashSegmentPreview = async (data) => {
    return apiRequest(`${FREE_CASH_ENDPOINT}/getSegmentPreview`, 'POST', data);
};

export const loadFreeCashSegment = async (data) => {
    return apiRequest(`${FREE_CASH_ENDPOINT}/loadSegment`, 'POST', data);
};

export const loadFreeCashCustom = async (data) => {
    return apiRequest(`${FREE_CASH_ENDPOINT}/loadCustom`, 'POST', data);
};

export const getFreeCashFilteredCustomers = async (data) => {
    return apiRequest(`${FREE_CASH_ENDPOINT}/getFilteredCustomers`, 'POST', data);
};

export const getFreeCashLoadHistory = async (data) => {
    return apiRequest(`${FREE_CASH_ENDPOINT}/getLoadHistory`, 'POST', data || {});
};
// **Free Cash Wallet END **

// ============================================
// UBER DIRECT - Local/Hyper-Local Delivery
// ============================================
const UBER_ENDPOINT = "/uber";

export const getUberDeliveryQuote = async (data) => {
    return apiRequest(`${UBER_ENDPOINT}/getQuote`, 'POST', data);
};

export const requestUberDelivery = async (data) => {
    return apiRequest(`${UBER_ENDPOINT}/requestDelivery`, 'POST', data);
};

export const getUberDeliveryStatus = async (data) => {
    return apiRequest(`${UBER_ENDPOINT}/getDeliveryStatus`, 'POST', data);
};

export const cancelUberDelivery = async (data) => {
    return apiRequest(`${UBER_ENDPOINT}/cancelDelivery`, 'POST', data);
};

export const getUberSettings = async () => {
    return apiRequest(`${UBER_ENDPOINT}/getSettings`, 'POST', {});
};

export const updateUberSettings = async (data) => {
    return apiRequest(`${UBER_ENDPOINT}/updateSettings`, 'POST', data);
};

export const listUberDeliveries = async (data) => {
    return apiRequest(`${UBER_ENDPOINT}/listDeliveries`, 'POST', data);
};
// **Uber Direct END **

// **Delivery App Controls START **
const DELIVERY_ENDPOINT = "/delivery";

export const getDeliverySettings = async () => {
    return apiRequest(`${DELIVERY_ENDPOINT}/settings/get`, 'POST', {});
};

export const updateDeliverySettings = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/settings/update`, 'POST', data);
};

export const createDeliveryDriver = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/drivers/create`, 'POST', data);
};

export const getDeliveryDrivers = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/drivers/get`, 'POST', data);
};

export const getDeliveryDriverById = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/drivers/getById`, 'POST', data);
};

export const updateDeliveryDriver = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/drivers/update`, 'POST', data);
};

export const verifyDeliveryDriver = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/drivers/verify`, 'POST', data);
};

export const deleteDeliveryDriver = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/drivers/delete`, 'POST', data);
};

export const recalculateDriverOrderCount = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/drivers/recalculateOrderCount`, 'POST', data || {});
};

export const getAvailableDrivers = async () => {
    return apiRequest(`${DELIVERY_ENDPOINT}/drivers/getAvailable`, 'POST', {});
};

export const assignDriverToOrder = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/orders/assign`, 'POST', data);
};

export const cancelDeliveryAssignment = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/orders/cancelAssignment`, 'POST', data);
};

export const getOrderTrackingInfo = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/orders/tracking`, 'POST', data);
};

export const getDeliveryDashboard = async () => {
    return apiRequest(`${DELIVERY_ENDPOINT}/dashboard`, 'POST', {});
};

export const getDeliveryLogs = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/logs/get`, 'POST', data);
};

export const getDeliveryLogsSummary = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/logs/getSummary`, 'POST', data || {});
};

export const getBroadcastLogs = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/orders/getBroadcastLogs`, 'POST', data);
};

export const broadcastOrderToDrivers = async (data) => {
    return apiRequest(`${DELIVERY_ENDPOINT}/orders/broadcast`, 'POST', data);
};
// **Delivery App Controls END **

// **Shiprocket Integration START **
const SHIPROCKET_ENDPOINT = "/shiprocket";

export const getShiprocketSettings = async () => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/settings/get`, 'POST', {});
};

export const updateShiprocketSettings = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/settings/update`, 'POST', data);
};

export const getShiprocketPickupAddresses = async () => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/pickup-addresses/get`, 'POST', {});
};

export const getShiprocketChannels = async () => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/channels/get`, 'POST', {});
};

export const createShiprocketShipment = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/shipment/create`, 'POST', data);
};

export const assignShiprocketAWB = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/shipment/assign-awb`, 'POST', data);
};

export const scheduleShiprocketPickup = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/shipment/schedule-pickup`, 'POST', data);
};

export const generateShiprocketLabel = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/shipment/generate-label`, 'POST', data);
};

export const generateShiprocketInvoice = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/shipment/generate-invoice`, 'POST', data);
};

export const cancelShiprocketShipment = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/shipment/cancel`, 'POST', data);
};

export const getShiprocketTracking = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/tracking/get`, 'POST', data);
};

export const getShiprocketCourierRates = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/courier/rates`, 'POST', data);
};

export const getShiprocketOrderCourierRates = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/courier/order-rates`, 'POST', data);
};

export const createShiprocketReturnOrder = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/returns/create`, 'POST', data);
};

export const generateShiprocketManifest = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/manifest/generate`, 'POST', data);
};

export const printShiprocketManifest = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/manifest/print`, 'POST', data);
};

export const getShiprocketNDRShipments = async () => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/ndr/list`, 'POST', {});
};

export const updateShiprocketNDRAction = async (data) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/ndr/action`, 'POST', data);
};

export const getShiprocketOrders = async (params) => {
    return apiRequest(`${SHIPROCKET_ENDPOINT}/orders/list`, 'POST', params || {});
};
// **Shiprocket Integration END **

// **Custom Order (Admin-initiated) START **
export const createCustomOrder = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/createCustomOrder`, 'POST', data);
};

export const validateCustomOrderBranch = async (data) => {
    return apiRequest(`${ORDERS_ENDPOINT}/validateCustomOrderBranch`, 'POST', data);
};
// **Custom Order END **

// **Zuntly GeoIQ — AI Store Expansion Intelligence START **
const GEOIQ_ENDPOINT = "/geoiq";

export const runGeoIqAnalysis = async (payload) => {
    return apiRequest(`${GEOIQ_ENDPOINT}/analyze`, 'POST', payload);
};

export const runGeoIqWhatIf = async (payload) => {
    return apiRequest(`${GEOIQ_ENDPOINT}/whatif`, 'POST', payload);
};

export const getGeoIqCompetitorDetails = async (placeId) => {
    return apiRequest(`${GEOIQ_ENDPOINT}/competitor-details`, 'POST', { placeId });
};
// **Zuntly GeoIQ END **

// **Zuntly AI — Merchant Revenue Intelligence START **
const ZUNTLY_AI_ENDPOINT = "/zuntly-ai";

export const zuntlyAiGetDailyUsage = async () => {
    return apiRequest(`${ZUNTLY_AI_ENDPOINT}/daily-usage`, 'GET');
};

export const runZuntlyAiAnalysis = async (payload) => {
    return apiRequest(`${ZUNTLY_AI_ENDPOINT}/analyze`, 'POST', payload);
};
// **Zuntly AI END **

// **Zuntly AI Cinematic Studio START **
const ZUNTLY_CINEMATIC_ENDPOINT = "/zuntly-cinematic";

export const getCinematicPresets = async () => {
    return apiRequest(`${ZUNTLY_CINEMATIC_ENDPOINT}/presets`, 'GET');
};

export const analyzeCinematicPhotos = async (payload) => {
    return apiRequest(`${ZUNTLY_CINEMATIC_ENDPOINT}/analyze-photos`, 'POST', payload || {});
};

export const generateCinematicProduction = async (payload) => {
    return apiRequest(`${ZUNTLY_CINEMATIC_ENDPOINT}/generate-production`, 'POST', payload || {});
};

export const synthesizeCinematicVoice = async (payload) => {
    return apiRequest(`${ZUNTLY_CINEMATIC_ENDPOINT}/synthesize-voice`, 'POST', payload || {});
};

/** Returns raw Response blob for MP4 download. Reports progress via onProgress. */
export const renderCinematicMp4 = async (payload, { onProgress } = {}) => {
    const API_BASE_URL = `${process.env.REACT_APP_API_URL}/zuntly/admin`;
    const headers = getProtectedAdminHeaders();
    const sealedBody = JSON.stringify(await wrapAdminPayload(payload || {}));

    const report = ( partial ) => {
        if (typeof onProgress === 'function') onProgress(partial);
    };

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let renderTimer = null;
        let softPercent = 8;

        const stopSoft = () => {
            if (renderTimer) {
                clearInterval(renderTimer);
                renderTimer = null;
            }
        };

        const startSoftRenderProgress = () => {
            stopSoft();
            softPercent = Math.max(softPercent, 12);
            report({ percent: Math.round(softPercent), phase: 'rendering', label: 'Rendering your film…' });
            renderTimer = setInterval(() => {
                // Asymptotic crawl toward 88% while server encodes
                const remain = 88 - softPercent;
                softPercent += Math.max(0.15, remain * 0.035);
                if (softPercent > 87.5) softPercent = 87.5;
                report({
                    percent: Math.round(softPercent),
                    phase: 'rendering',
                    label: 'Rendering scenes, voice & subtitles…',
                });
            }, 450);
        };

        xhr.open('POST', `${API_BASE_URL}${ZUNTLY_CINEMATIC_ENDPOINT}/render-mp4`);
        Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.responseType = 'blob';
        xhr.timeout = 15 * 60 * 1000;

        xhr.upload.onprogress = (e) => {
            if (!e.lengthComputable) return;
            const pct = Math.min(10, Math.round((e.loaded / e.total) * 10));
            softPercent = Math.max(softPercent, pct);
            report({ percent: pct, phase: 'uploading', label: 'Sending production…' });
        };

        xhr.upload.onload = () => {
            startSoftRenderProgress();
        };

        xhr.onprogress = (e) => {
            if (!e.lengthComputable || e.total <= 0) return;
            stopSoft();
            const dlPct = 88 + Math.round((e.loaded / e.total) * 11);
            softPercent = Math.max(softPercent, dlPct);
            report({
                percent: Math.min(99, softPercent),
                phase: 'downloading',
                label: 'Downloading MP4…',
                loaded: e.loaded,
                total: e.total,
            });
        };

        xhr.onload = () => {
            stopSoft();
            if (xhr.status < 200 || xhr.status >= 300) {
                let message = 'MP4 render failed';
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const err = JSON.parse(String(reader.result || '{}'));
                        message = err.message || message;
                    } catch { /* ignore */ }
                    reject(new Error(message));
                };
                reader.onerror = () => reject(new Error(message));
                if (xhr.response) reader.readAsText(xhr.response);
                else reject(new Error(message));
                return;
            }
            report({ percent: 100, phase: 'complete', label: 'Ready' });
            const disposition = xhr.getResponseHeader('Content-Disposition') || '';
            const match = disposition.match(/filename="([^"]+)"/);
            resolve({
                blob: xhr.response,
                fileName: match?.[1] || 'zuntly-cinematic.mp4',
            });
        };

        xhr.onerror = () => {
            stopSoft();
            reject(new Error('Network error while rendering MP4'));
        };
        xhr.ontimeout = () => {
            stopSoft();
            reject(new Error('MP4 render timed out'));
        };
        xhr.onabort = () => {
            stopSoft();
            reject(new Error('MP4 download cancelled'));
        };

        report({ percent: 2, phase: 'starting', label: 'Starting render…' });
        xhr.send(sealedBody);
    });
};

export const getCinematicProxyImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    const API_BASE_URL = `${process.env.REACT_APP_API_URL}/zuntly/admin`;
    const params = new URLSearchParams({ url: imageUrl });
    return `${API_BASE_URL}${ZUNTLY_CINEMATIC_ENDPOINT}/proxy-image?${params.toString()}`;
};
// **Zuntly AI Cinematic Studio END **

// **Zuntly Marketplace (B2B) — Super Admin settings + portal orders START **
const MARKETPLACE_ENDPOINT = "/marketplace";

export const getMarketplaceSettings = async () => {
    return apiRequest(`${MARKETPLACE_ENDPOINT}/getSettings`, 'POST', {});
};

export const updateMarketplaceSettings = async (data) => {
    return apiRequest(`${MARKETPLACE_ENDPOINT}/updateSettings`, 'POST', data);
};

export const getMarketplaceMasters = async () => {
    return apiRequest(`${MARKETPLACE_ENDPOINT}/getMasters`, 'POST', {});
};

export const getMasterMarketplaceProducts = async (masterId) => {
    return apiRequest(`${MARKETPLACE_ENDPOINT}/getMasterProducts`, 'POST', { masterId });
};

export const getMarketplaceOrders = async (data) => {
    return apiRequest(`${MARKETPLACE_ENDPOINT}/getOrders`, 'POST', data || {});
};

export const updateMarketplaceOrderStatus = async (data) => {
    return apiRequest(`${MARKETPLACE_ENDPOINT}/updateOrderStatus`, 'POST', data);
};
// **Zuntly Marketplace END **

// **Omni Commerce (Unified multi-channel hub) START **
const OMNI_COMMERCE_ENDPOINT = '/omni-commerce';

export const getOmniPlatformCatalog = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getPlatformCatalog`, 'POST', data || {});

export const getOmniConnections = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getConnections`, 'POST', data || {});

export const getOmniConnectionSchemas = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getConnectionSchemas`, 'POST', data || {});

export const connectOmniChannel = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/connectChannel`, 'POST', data || {});

export const disconnectOmniChannel = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/disconnectChannel`, 'POST', data || {});

export const syncOmniNow = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/syncNow`, 'POST', data || {});

export const getOmniUnifiedOrders = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getUnifiedOrders`, 'POST', data || {});

export const getOmniDashboard = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getDashboard`, 'POST', data || {});

export const getOmniSyncLogs = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getSyncLogs`, 'POST', data || {});

export const getOmniAuditLogs = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getAuditLogs`, 'POST', data || {});

export const getOmniAnalytics = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getAnalytics`, 'POST', data || {});

export const getOmniReturns = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getReturns`, 'POST', data || {});

export const getOmniCancellations = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getCancellations`, 'POST', data || {});

export const getOmniShipping = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/getShipping`, 'POST', data || {});

export const performOmniOrderAction = async (data) =>
    apiRequest(`${OMNI_COMMERCE_ENDPOINT}/performOrderAction`, 'POST', data || {});
// **Omni Commerce END **

// **POS Behaviour Payment (Merchant Billing) — toggle config only **
const PIP_ENDPOINT = '/pip';

export const pipGetConfig = async (data) =>
    apiRequest(`${PIP_ENDPOINT}/config/get`, 'POST', data || {});

export const pipUpdateConfig = async (data) =>
    apiRequest(`${PIP_ENDPOINT}/config/update`, 'POST', data || {});

